import {
  addressSchema,
  assistantAddressSchema,
  contractTypeSchema,
  pictureSchema,
  signatureUrlSchema,
  UnauthorizedError,
  userRoleSchema,
} from '@omecare/utils';
import { UseQueryResult, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import httpService from '@/utils/http.service';
import { getDoctorSubtituteResponseSchema } from './use-get-doctor-substitute';

// Medical assistant profile shape on the `me` response, present when the
// current user has the `medical_assistant` role.
const medicalAssistantProfileMeSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  firstname: z.string(),
  lastname: z.string(),
  phone: z.string().nullable().default(null),
  contractType: contractTypeSchema,
  weeklyHours: z.number().int(),
  hireDate: z.coerce.date(),
  dateOfBirth: z.coerce.date().nullable().default(null),
  birthPlace: z.string().nullable().default(null),
  nationality: z.string().nullable().default(null),
  socialSecurityNumber: z.string().nullable().default(null),
  address: assistantAddressSchema.nullable().default(null),
});

export type MedicalAssistantProfileMe = z.infer<
  typeof medicalAssistantProfileMeSchema
>;

// Doctor attending schema - includes optional profile fields on first item
const doctorAttendingMeSchema = z.object({
  id: z.string().uuid(),
  officeId: z.string().uuid(),
  officeName: z.string(),
  // Optional doctor profile fields (only on first item since shared at Doctor level)
  dateOfBirth: z.coerce.date().nullable().optional(),
  address: addressSchema.nullable().optional(),
  orderRegistrationNumber: z.string().optional(),
  rppsNumber: z.string().optional(),
  thesis: z.boolean().optional(),
  rhythm: z.string().nullable().optional(),
  onSiteSlotsWithoutAssistant: z.number().int().nullable().optional(),
  remoteSlotsWithoutAssistant: z.number().int().nullable().optional(),
  onSiteSlotsWithAssistant: z.number().int().nullable().optional(),
  remoteSlotsWithAssistant: z.number().int().nullable().optional(),
  rhythmNote: z.string().nullable().optional(),
  iban: z.string().optional(),
  bic: z.string().nullable().optional(),
  signatureUrl: signatureUrlSchema.optional(),
});

export type DoctorAttendingMe = z.infer<typeof doctorAttendingMeSchema>;

export const meResponseSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  picture: pictureSchema,
  role: userRoleSchema,
  firstname: z.string(),
  lastname: z.string(),
  phoneNumber: z.string().nullable().default(null),
  siteIds: z.array(z.string().uuid()).default([]),
  officeIds: z.array(z.string().uuid()).default([]),
  doctorSubstitute: getDoctorSubtituteResponseSchema
    .omit({
      siteAccesses: true,
      rhythm: true,
    })
    .optional(),
  doctorAttending: z.array(doctorAttendingMeSchema).optional().default([]),
  medicalAssistantProfile: medicalAssistantProfileMeSchema.optional(),
});

export type GetMeResponse = z.infer<typeof meResponseSchema>;

const getMe = async (): Promise<GetMeResponse> => {
  return httpService.users.getMe(undefined).then(({ response, transform }) => {
    return transform.handle({
      response,
      attributes: {
        email: true,
        role: true,
        firstname: true,
        lastname: true,
        picture: true,
        phoneNumber: true,
        siteIds: true,
        officeIds: true,
      },
      relations: {
        doctorSubstitute: true,
        doctorAttending: true,
        medicalAssistantProfile: true,
      },
      schema: meResponseSchema,
    }) as GetMeResponse;
  });
};

export const useGetMeQuery = (options?: {
  enabled?: boolean;
}): UseQueryResult<GetMeResponse, UnauthorizedError> => {
  return useQuery({
    queryFn: getMe,
    queryKey: ['me'],
    retry: false,
    enabled: options?.enabled ?? true,
    // `me.doctorAttending` and `me.officeIds` change during the session when
    // the user is added to / removed from an office. Mutations that touch
    // those relations invalidate `['me']` explicitly; the short staleTime
    // covers out-of-band changes (e.g. admin added the user from another
    // session) without thrashing.
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
};
